import React, { useCallback, useEffect, useRef, useState } from 'react';
import { dueChip, fmtDateTime, relative } from '../../lib/when';
import { Icon } from './icons';
import { Avatar, Dialog, GrowingTextarea } from './TodoPeople';
import { useConfirm } from '../ConfirmDialog';
import TodoDeps, { LockIcon } from './TodoDeps';
import { ClientChip, pickable } from './TodoClients';
import { durationLabel, longDay } from '../../lib/quickAdd';
import { deadlineState } from '../todoViews';

/*
 * When something was said, the way a feed says it: "Just now", "3 h ago",
 * "Yesterday 15:00", "12 Oct" (lib/when). The exact moment is the <time>'s
 * title, for whoever hovers.
 */
const when = (iso) => relative(iso);
const exact = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return undefined;
  return d.toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false });
};

/* A finger, not a pointer: no Enter-to-send hint, and no keyboard thrown up on open. */
const touch = () => { try { return window.matchMedia('(hover: none)').matches; } catch { return false; } };

const isoToday = (now) => `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

/* What happened, in words, for a kind the server sent without any. */
const DID = {
  created: 'made it',
  assigned: 'gave it to somebody',
  unassigned: 'took it off whoever had it',
  sent: 'sent it on',
  commented: 'commented',
  completed: 'ticked it off',
  done: 'ticked it off',
  reopened: 'reopened it',
  moved: 'moved it',
  edited: 'changed it',
  renamed: 'renamed it',
  shared: 'shared the list',
  unshared: 'took somebody off the list',
  left: 'left the list',
  attached: 'added a file',
  // 072
  blocked: 'made it wait on another to-do',
  unblocked: 'stopped it waiting on another to-do',
  ready: 'finished the last thing it waited on - it is ready to start',
  client: 'changed the client'
};

const SOURCE_SAID = { direct: 'from a chat message', group: 'from a group message',
  ticket_comment: 'from a ticket comment', ticket: 'from a ticket' };

/*
 * An event in words. The server sends `detail` as it stored it - todo_events
 * .detail, a JSON object whose fields depend on the kind ({ to_key, to_name }
 * for assigned and sent, { comment_id, excerpt } for commented, { from_name,
 * to_name } for moved, { file_name } for attached, { source_kind } for created
 * ...). This page was written against words in `detail`, and React refuses to
 * render an object - the first Activity tab opened on the real server took the
 * whole app down ("Jarvis stopped rendering"). So: words when they are words,
 * a sentence built from the object when it is one, and the kind's own words
 * otherwise. Never the object itself.
 */
export const activityWords = (a) => {
  const d = a?.detail;
  if (typeof d === 'string' && d.trim()) return d;
  const base = DID[a?.kind] || a?.kind || 'changed it';
  if (!d || typeof d !== 'object' || Array.isArray(d)) return base;
  const s = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
  switch (a.kind) {
    // Converted in place (the server logs it as 'created', with which way).
    case 'created': return d.converted_from === 'note' ? 'turned it from a note into a to-do'
      : d.converted_to === 'note' ? 'turned it into a note'
      : SOURCE_SAID[d.source_kind] ? `made it ${SOURCE_SAID[d.source_kind]}`
      // Made by applying a template (071).
      : s(d.template_name) ? `made it from the template “${s(d.template_name)}”` : base;
    // "Gordon gave it to Gordon" reads as a glitch; picking yourself is taking it on.
    case 'assigned': return !s(d.to_name) ? base
      : s(d.to_name) === s(a.actor_name) ? 'took it on' : `gave it to ${s(d.to_name)}`;
    case 'unassigned': return s(d.name) ? `took it off ${s(d.name)}` : base;
    case 'sent': return s(d.to_name)
      ? `sent it to ${s(d.to_name)}${s(d.note) ? `: “${s(d.note)}”` : ''}` : base;
    case 'commented': return s(d.excerpt) ? `commented: “${s(d.excerpt)}”` : base;
    case 'moved': return s(d.to_name) ? `moved it${s(d.from_name) ? ` from ${s(d.from_name)}` : ''} to ${s(d.to_name)}` : base;
    case 'attached': return s(d.file_name) ? `added ${s(d.file_name)}` : base;
    case 'shared': return s(d.name) ? `shared the list with ${s(d.name)}` : base;
    // 072. A title the viewer may not see is not sent, so the words fall back.
    case 'blocked': return s(d.blocker_title) ? `made it wait on “${s(d.blocker_title)}”` : base;
    case 'unblocked': return s(d.blocker_title) ? `stopped it waiting on “${s(d.blocker_title)}”` : base;
    case 'ready': return s(d.after_title) ? `finished “${s(d.after_title)}” - it is ready to start` : base;
    case 'client': return d.cleared ? 'took the client off' : s(d.name) ? `made it for ${s(d.name)}` : base;
    case 'completed': return Number(d.while_waiting_on) > 0
      ? `ticked it off while it was still waiting on ${Number(d.while_waiting_on) === 1 ? 'a to-do' : `${Number(d.while_waiting_on)} to-dos`}`
      : base;
    default: return base;
  }
};

/**
 * A message box that sends on Enter and takes a new line on Shift+Enter, as
 * chat does. One line when empty, growing as it is typed into. A new comment
 * has Send beside the box; an edit has Cancel and Save under it.
 */
function Composer({ initial = '', label, placeholder, submit, busy, onSubmit, onCancel = null, autoFocus = false }) {
  const [text, setText] = useState(initial);
  const box = useRef(null);
  const send = () => {
    const body = text.trim();
    if (!body || busy) return;
    Promise.resolve(onSubmit(body)).then((ok) => { if (ok !== false && !onCancel) setText(''); });
  };
  // An edit opens with the caret at the end of what was said, not the start.
  useEffect(() => {
    if (!autoFocus || !onCancel || !box.current) return;
    const n = box.current.value.length;
    try { box.current.setSelectionRange(n, n); } catch { /* not a text box */ }
  }, [autoFocus, onCancel]);
  return (
    <form className={`tds-compose${onCancel ? ' is-edit' : ' is-new'}`} onSubmit={(e) => { e.preventDefault(); send(); }}>
      <GrowingTextarea ref={box} rows={1} value={text} aria-label={label} placeholder={placeholder} maxLength={4000}
                       autoFocus={autoFocus}
                       onChange={(e) => setText(e.target.value)}
                       onKeyDown={(e) => {
                         if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); }
                         if (e.key === 'Escape' && onCancel) { e.preventDefault(); e.stopPropagation(); onCancel(); }
                       }} />
      <span className="tds-compose-buttons">
        {!onCancel && !touch() && (
          <span className="tds-compose-hint" aria-hidden="true">Enter to send · Shift+Enter for a new line</span>
        )}
        {onCancel && <button type="button" className="btn btn-outline tds-btn is-small" onClick={onCancel}>Cancel</button>}
        <button type="submit" className="btn btn-primary tds-btn is-small" disabled={busy || !text.trim()}>{submit}</button>
      </span>
    </form>
  );
}

/** "Today 15:00 · ● Site visits": the to-do's day and list, under its title in the panel. */
function Meta({ item, list, onEdit = null }) {
  const now = new Date();
  const day = item.due_date ? String(item.due_date).slice(0, 10) : null;
  const today = isoToday(now);
  const state = !day || item.done ? '' : day < today ? ' is-late' : day === today ? ' is-today' : '';
  const name = list && list.name ? list.name : null;
  // 073: the deadline, and how long it takes.
  const deadline = item.deadline ? String(item.deadline).slice(0, 10) : null;
  const dlState = !deadline || item.done ? null : deadlineState(item, today);
  const takes = Number(item.duration_minutes) > 0 ? durationLabel(item.duration_minutes) : null;
  if (!day && !name && !deadline && !takes && !onEdit) return null;
  return (
    <p className="tds-meta">
      {day && (
        <span className={`tds-due${state}`} data-due={day}>
          <Icon name="calendar" size={14} />
          {dueChip(day, item.due_time || null, now)}
        </span>
      )}
      {takes && (
        <span className="tds-due tds-takes" data-duration={item.duration_minutes}>
          <Icon name="duration" size={14} />{takes}
        </span>
      )}
      {deadline && (
        <span className={`tds-due tds-deadline${dlState ? ` is-${dlState}` : ''}`} data-deadline={deadline}>
          <Icon name="deadline" size={14} />{`Deadline ${longDay(deadline)}`}
        </span>
      )}
      {onEdit && item.access !== 'delegated' && (
        <button type="button" className="btn btn-secondary btn-small tds-edit" onClick={() => onEdit(item)}>
          <Icon name="edit" size={13} /> Edit
        </button>
      )}
      {name && (
        <span className="tds-meta-list">
          {list.is_inbox ? <Icon name="inbox" size={14} />
            : <span className="tds-meta-dot" aria-hidden="true"
                    style={list.color ? { '--tds-dot': `var(--color-swatch-${list.color})` } : undefined} />}
          {name}
        </span>
      )}
    </p>
  );
}

/**
 * WHO HAS IT. In a shared list, any member - me included - by a picker that
 * changes it at once. In a list of my own nobody else can see it, so giving
 * it to somebody means SENDING it to them ("Send to someone…"). A to-do I
 * have already sent is theirs now: here it only says who has it.
 */
function Assignment({ item, list, myKey, busy, canSend, onAssign, onSend }) {
  const by = item.assigned_by_name && item.assigned_by_key !== myKey ? item.assigned_by_name : null;
  if (item.access === 'delegated') {
    return (
      <div className="tds-assign" data-assign="delegated">
        <span className="tds-assign-label">With</span>
        {item.assignee_name && <Avatar name={item.assignee_name} who={item.assignee_key} />}
        <span className="tds-name">{item.assignee_name || 'somebody else'}</span>
        <span className={`tds-state is-${item.done ? 'done' : 'open'}`}>{item.done ? 'Done' : 'Open'}</span>
      </div>
    );
  }
  // 089: in Private nothing is given or sent - the lock says so instead.
  if (list && list.is_private) {
    return (
      <div className="tds-assign tds-private" data-assign="private-list">
        <LockIcon size={14} />
        <span className="tds-quiet">Private — only you can see this to-do. It cannot be given to anybody or sent.</span>
      </div>
    );
  }
  const members = list && Array.isArray(list.members) ? list.members : [];
  if (list && list.shared) {
    const known = members.some((m) => m.key === item.assignee_key);
    return (
      <div className="tds-assign" data-assign="shared">
        <label className="tds-assign-pick">
          <span className="tds-assign-label">Assigned to</span>
          <select aria-label="Assigned to" value={item.assignee_key || ''} disabled={busy}
                  onChange={(e) => onAssign(item, e.target.value || null)}>
            <option value="">Nobody</option>
            {!known && item.assignee_key && <option value={item.assignee_key}>{item.assignee_name || 'Somebody'}</option>}
            {members.map((m) => (
              <option key={m.key} value={m.key}>{m.name}{m.key === myKey ? ' (you)' : ''}</option>
            ))}
          </select>
        </label>
        {by && <span className="tds-quiet">by {by}</span>}
        {canSend && !item.done && (
          <button type="button" className="tds-link" disabled={busy} onClick={() => onSend(item)}>
            Send to someone outside it…
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="tds-assign" data-assign="private">
      {item.assignee_key && item.assignee_key !== myKey ? (
        <>
          <span className="tds-assign-label">Assigned to</span>
          <Avatar name={item.assignee_name} who={item.assignee_key} />
          <span className="tds-name">{item.assignee_name}</span>
        </>
      ) : by ? (
        <span className="tds-quiet">Given to you by {by}</span>
      ) : null}
      {canSend && !item.done && (
        <button type="button" className="tds-link" disabled={busy} onClick={() => onSend(item)}>
          Send to someone…
        </button>
      )}
    </div>
  );
}

/**
 * ONE TO-DO'S CONVERSATION: who has it, where it came from, the comments on
 * it and what has happened to it. Opened from a row's 💬 as a panel of its
 * own, and shown under the editor when the to-do is being edited.
 *
 * COMMENTS are Todoist's: a thread under the to-do, mine editable and
 * removable, Enter to send and Shift+Enter for a new line, as in chat. On a
 * shared to-do it is how two people talk about one job without a chat thread
 * that loses which job it was. ACTIVITY is the record - made, given, sent,
 * ticked - read only when its tab is opened.
 *
 * FROM: a to-do made from a chat message or a ticket says so, and links back
 * to it, so "ring him back" still knows who him was.
 *
 * `api` is the page's: paths under /todos, with `as=` when an administrator
 * is looking through a contractor's eyes.
 */
export default function TodoDetail({ item, list = null, myKey = null, api, inline = false, busy = false, canSend = true,
                                     startOn = 'comments', onClose, onCount, onAssign, onSend, onGo,
                                     deps = false, candidates = [], onDepsChanged = null,
                                     clients = null, isAdmin = false, onClient = null, onEdit = null,
                                     onFlag = null, assist = null, parts = null }) {
  const [tab, setTab] = useState(startOn === 'activity' ? 'activity' : 'comments');
  const [comments, setComments] = useState(null);
  const [activity, setActivity] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState(null);
  const [confirm, confirmDialog] = useConfirm();
  const listEnd = useRef(null);
  const id = item.id;

  const loadComments = useCallback(() => api.get(`/${id}/comments`)
    .then((r) => setComments(Array.isArray(r.data) ? r.data : []))
    .catch((err) => { setComments([]); setProblem(err.response?.data?.error || 'Could not load the comments'); }),
  [api, id]);

  useEffect(() => { loadComments(); }, [loadComments]);
  useEffect(() => {
    if (tab !== 'activity' || activity !== null) return;
    api.get(`/${id}/activity`)
      .then((r) => setActivity(Array.isArray(r.data) ? r.data : []))
      .catch(() => setActivity([]));
  }, [tab, activity, api, id]);

  // The count on the row follows the thread, without reading the list again.
  const counted = useRef(null);
  useEffect(() => {
    if (comments === null || !onCount) return;
    if (counted.current === null) { counted.current = comments.length; return; }
    if (counted.current !== comments.length) { counted.current = comments.length; onCount(id, comments.length); }
  }, [comments, onCount, id]);

  const run = async (fn, fail) => {
    setSaving(true);
    setProblem(null);
    try { await fn(); return true; }
    catch (err) { setProblem(err.response?.data?.error || fail); return false; }
    finally { setSaving(false); }
  };

  const add = (body) => run(async () => {
    const r = await api.post(`/${id}/comments`, { body });
    const row = r.data && r.data.id ? r.data : { id: `tmp-${Date.now()}`, body, mine: true, created_at: new Date().toISOString() };
    setComments((prev) => [...(prev || []), { mine: true, ...row }]);
    setActivity(null);
    requestAnimationFrame(() => { if (listEnd.current && listEnd.current.scrollIntoView) listEnd.current.scrollIntoView({ block: 'nearest' }); });
  }, 'That comment did not go through');

  const edit = (c, body) => {
    if (body === c.body) { setEditingId(null); return Promise.resolve(true); }
    return run(async () => {
      const r = await api.patch(`/comments/${c.id}`, { body });
      setComments((prev) => prev.map((x) => (x.id === c.id
        ? { ...x, ...(r.data && r.data.id ? r.data : {}), body, edited_at: (r.data && r.data.edited_at) || new Date().toISOString() }
        : x)));
      setEditingId(null);
    }, 'That change did not go through');
  };

  const remove = async (c) => {
    if (!(await confirm({ title: 'Delete this comment?', confirmLabel: 'Delete', danger: true }))) return;
    run(async () => {
      await api.del(`/comments/${c.id}`);
      setComments((prev) => prev.filter((x) => x.id !== c.id));
    }, 'That comment could not be deleted');
  };

  const src = item.source && (item.source.label || item.source.href) ? item.source : null;
  const n = comments ? comments.length : Number(item.comment_count) || 0;

  const body = (
    <div className={`tds-detail${inline ? ' is-inline' : ''}`} data-detail={id}>
      {!inline && <Meta item={item} list={list} onEdit={onEdit} />}
      {/* 097: it arrived by email. */}
      {item.origin === 'email' && (
        <p className="tds-source tds-origin" data-source="email" data-origin-from={item.origin_from || ''}>
          <Icon name="send" size={13} />
          <span className="tds-quiet">From an email</span>{' '}
          {item.origin_from && <span>sent by <strong>{item.origin_from}</strong></span>}
          {item.origin_at && <span className="tds-quiet"> on {fmtDateTime(item.origin_at)}</span>}
        </p>
      )}
      {src && (
        <p className="tds-source" data-source={src.kind || ''}>
          <span className="tds-quiet">From</span>{' '}
          {src.href ? (
            <a href={src.href} onClick={(e) => {
              if (onGo && String(src.href).startsWith('/')) { e.preventDefault(); onGo(src.href); }
            }}>{src.label || 'where it came from'}</a>
          ) : <span>{src.label}</span>}
        </p>
      )}
      {/* What is IN it: its words (in the panel - under the editor and in a
          board card they are already on screen), its checklist and its
          files (TodoParts.js). A note made a to-do keeps all three. */}
      {!inline && item.notes && <p className="tdp-notes" data-detail-notes="">{item.notes}</p>}
      {parts}
      <Assignment item={item} list={list} myKey={myKey} busy={busy || saving} canSend={canSend}
                  onAssign={onAssign} onSend={onSend} />
      {/* 072: the client (under the editor it is the editor's field), and
          what it waits on and holds up. */}
      {!inline && clients && onClient && item.access !== 'delegated' ? (
        <div className="tds-assign tds-client" data-client-field="">
          <label className="tds-assign-pick">
            <span className="tds-assign-label">Client</span>
            <select aria-label="Client" value={item.company_id || ''} disabled={busy || saving}
                    title={isAdmin ? 'Clients are the companies in Settings → Assets → Companies'
                      : 'Not there? Ask an admin to add it in Settings → Assets → Companies'}
                    onChange={(e) => onClient(item, e.target.value || null)}>
              <option value="">No client</option>
              {pickable(clients, item.company || null).map((c) => (
                <option key={c.id} value={c.id} disabled={c.archived && c.id !== item.company_id}>
                  {c.name}{c.short_code ? ` (${c.short_code})` : ''}{c.archived ? ' - archived' : ''}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : !inline && item.company ? (
        <div className="tds-assign tds-client"><span className="tds-assign-label">Client</span> <ClientChip company={item.company} full /></div>
      ) : null}
      {/* 074: the flag - on the to-do, so everybody on its list sees it. */}
      {onFlag && item.access !== 'delegated' && !item.done_at && (
        <div className="tds-assign tdf-detail-flag" data-flag-field="">
          <span className="tds-assign-label">Flag</span>
          <button type="button" className={`tdf-flag-toggle${item.flagged ? ' is-on' : ''}`} aria-pressed={Boolean(item.flagged)}
                  disabled={busy || saving} onClick={() => onFlag(item)}>
            <Icon name={item.flagged ? 'flagged' : 'flag'} size={14} />
            {item.flagged ? 'Flagged' : 'Not flagged'}
          </button>
        </div>
      )}
      {deps && <TodoDeps item={item} api={api} candidates={candidates} busy={busy || saving} onChanged={onDepsChanged} />}
      {/* Batch C: AI help - Break into steps, Checklist from text (TodoAssist.js). */}
      {assist}

      <div className="tds-tabs" role="tablist" aria-label="Comments and activity">
        <button type="button" role="tab" aria-selected={tab === 'comments'} className="tds-tab"
                onClick={() => setTab('comments')}>
          Comments{n ? <span className="tds-tab-n">{n}</span> : null}
        </button>
        <button type="button" role="tab" aria-selected={tab === 'activity'} className="tds-tab"
                onClick={() => setTab('activity')}>Activity</button>
      </div>
      {problem && <p className="tds-error" role="alert">{problem}</p>}

      {tab === 'comments' ? (
        <div className="tds-thread" role="tabpanel" aria-label="Comments">
          <div className="tds-scroll">
            {comments === null ? <p className="tds-quiet">Loading…</p>
              : comments.length === 0 ? (
                <p className="tds-empty">
                  <Icon name="comment" size={20} />
                  <span>No comments yet. Anything said here stays with this to-do.</span>
                </p>
              ) : (
                <ul className="tds-comments">
                  {comments.map((c) => (
                    <li key={c.id} className={`tds-comment${editingId === c.id ? ' is-editing' : ''}`} data-comment={c.id}
                        data-mine={c.mine ? '' : undefined}>
                      <Avatar name={c.mine ? (c.author_name || 'You') : c.author_name} who={c.author_key} />
                      <div className="tds-comment-main">
                        <p className="tds-comment-head">
                          <span className="tds-name">{c.mine ? 'You' : c.author_name || 'Somebody'}</span>
                          <time dateTime={c.created_at || undefined} title={exact(c.created_at)}>{when(c.created_at)}</time>
                          {c.edited_at && <span className="tds-edited">(edited)</span>}
                          {/* Mine only, and quiet: at the end of the name line, shown
                              when the comment is hovered or focused into (always, a
                              shade lighter, on a touch screen). */}
                          {c.mine && editingId !== c.id && (
                            <span className="tds-comment-tools">
                              <button type="button" className="tds-icon-btn" onClick={() => setEditingId(c.id)}
                                      aria-label="Edit your comment" title="Edit">
                                <Icon name="edit" size={15} />
                              </button>
                              <button type="button" className="tds-icon-btn is-danger" onClick={() => remove(c)}
                                      aria-label="Delete your comment" title="Delete">
                                <Icon name="trash" size={15} />
                              </button>
                            </span>
                          )}
                        </p>
                        {editingId === c.id ? (
                          <Composer initial={c.body} label="Edit your comment" submit="Save" busy={saving} autoFocus
                                    onSubmit={(b) => edit(c, b)} onCancel={() => setEditingId(null)} />
                        ) : (
                          <p className="tds-comment-body">{c.body}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            <span ref={listEnd} />
          </div>
          {/* Opened from its checklist or files chip ('parts'), the panel stays
              at the top, on them - not scrolled down to the comment box. */}
          <Composer label="Add a comment" placeholder="Add a comment…"
                    submit="Send" busy={saving} onSubmit={add}
                    autoFocus={!inline && !touch() && startOn !== 'parts'} />
        </div>
      ) : (
        <div className="tds-thread" role="tabpanel" aria-label="Activity">
          <div className="tds-scroll">
            {activity === null ? <p className="tds-quiet">Loading…</p>
              : activity.length === 0 ? <p className="tds-quiet">Nothing has happened to it yet.</p> : (
                <ul className="tds-activity">
                  {activity.map((a, i) => (
                    <li key={`${a.created_at}-${i}`} className="tds-event" data-kind={a.kind || ''}>
                      <Avatar name={a.actor_name || 'Somebody'} who={a.actor_key || a.actor_name} />
                      <span className="tds-event-text">
                        <span className="tds-name">{a.actor_name || 'Somebody'}</span>{' '}
                        <span>{activityWords(a)}</span>
                      </span>
                      <time className="tds-event-when" dateTime={a.created_at || undefined} title={exact(a.created_at)}>
                        {when(a.created_at)}
                      </time>
                    </li>
                  ))}
                </ul>
              )}
          </div>
        </div>
      )}
      {confirmDialog}
    </div>
  );

  if (inline) return body;
  return (
    <Dialog label={item.title} onClose={onClose} className="tds-panel">
      {body}
    </Dialog>
  );
}
